import React from 'react';
import cn from 'classnames';
import { arrayOf, func, shape, string, object, bool, number } from 'prop-types';
import { t } from 'i18next';
import { FormControl, FormControlLabel, Radio, RadioGroup, FormHelperText } from '@mui/material';
import { timeOutline, warningOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';

import { stringOrNumberPropTypes } from '~utils/propTypes';

import styles from './styles.module.scss';
import { AS_SOON_AS_POSSIBLE, SCHEDULE_TIME, DEFAULT_TIME_SELECTION } from './constants';
import SelectTime from './SelectTime.js';

// eslint-disable-next-line complexity
function SelectTimeDispatch({
  timeOptions,
  selectedTime,
  onSelectTime,
  deliveryOpen,
  deliveryWaitTime,
  handleChangeRadioButton,
  radioButtonSelected,
  futureOrdersEnabled,
  dispatchDay,
  pickUp,
  pickUpOpen
}) {
  return (
    <FormControl fullWidth className={styles.formContainer}>
      <RadioGroup value={radioButtonSelected} onChange={handleChangeRadioButton}>
        {((!pickUp && deliveryOpen) || (pickUp && pickUpOpen)) && (
          <div
            className={cn('row space-between middle full-width', styles.timeSchedulingContainer, {
              [styles.timeSchedulingContainerSelected]: radioButtonSelected === AS_SOON_AS_POSSIBLE
            })}>
            <FormControlLabel
              value={AS_SOON_AS_POSSIBLE}
              control={
                <Radio
                  className={cn(styles.radioButton, {
                    [styles.radioButtonSelected]: radioButtonSelected === AS_SOON_AS_POSSIBLE
                  })}
                  size="small"
                />
              }
              label={t('SelectTimeDispatch:asSoonAsPossible')}
              className={cn({
                [styles.selectedLabel]: radioButtonSelected === AS_SOON_AS_POSSIBLE
              })}
            />
            {!pickUp && deliveryOpen && (
              <div className="row middle">
                <IonIcon
                  className={cn('m-right-1', {
                    [styles.deliveryTimeColor]: radioButtonSelected === AS_SOON_AS_POSSIBLE
                  })}
                  icon={timeOutline}
                />
                <span
                  className={cn('bold', styles.deliveryTime, {
                    [styles.deliveryTimeColor]: radioButtonSelected === AS_SOON_AS_POSSIBLE
                  })}>
                  {t('Delivery:time', { time: deliveryWaitTime })}
                </span>
              </div>
            )}
          </div>
        )}
        {futureOrdersEnabled && timeOptions && (
          <div
            className={cn(
              'row space-between middle full-width',
              styles.timeSchedulingContainer,
              styles.futureOrderButton,
              {
                [styles.timeSchedulingContainerSelected]: radioButtonSelected === SCHEDULE_TIME
              }
            )}>
            <div className={`${styles.relative} column`}>
              <FormControlLabel
                value={SCHEDULE_TIME}
                control={
                  <Radio
                    className={cn(styles.radioButton, {
                      [styles.radioButtonSelected]: radioButtonSelected === SCHEDULE_TIME
                    })}
                    size="small"
                  />
                }
                label={t('SelectTimeDispatch:later')}
                className={cn(styles.laterRadio, {
                  [styles.selectedLabel]: radioButtonSelected === SCHEDULE_TIME
                })}
              />
              <FormHelperText
                className={cn(styles.chooseTimeHelperText, {
                  [styles.active]: radioButtonSelected === SCHEDULE_TIME
                })}>
                {DEFAULT_TIME_SELECTION.value === selectedTime.value
                  ? selectedTime?.name
                  : `${dispatchDay.text}, ${selectedTime?.name}`}
              </FormHelperText>
            </div>
            <SelectTime
              timeOptions={timeOptions}
              handleChange={onSelectTime}
              selectedTime={selectedTime}
              dispatchDay={dispatchDay}
              disabled={radioButtonSelected !== SCHEDULE_TIME}
            />
          </div>
        )}
      </RadioGroup>
      {radioButtonSelected === SCHEDULE_TIME && futureOrdersEnabled && (
        <div
          className={cn('row middle full-width m-top-2', {
            [styles.warningContainer]: !pickUp,
            [styles.timeSchedulingContainer]: !pickUp
          })}>
          <IonIcon
            className={cn('m-right-2 subtitle-small', {
              [styles.textWarning]: !pickUp,
              [styles.textWarningPickUp]: pickUp
            })}
            icon={warningOutline}
          />
          <span
            className={cn({
              [styles.textWarning]: !pickUp,
              [styles.textWarningPickUp]: pickUp
            })}>
            {t('SelectTimeDispatch:futureOrderWarning')}
          </span>
        </div>
      )}
    </FormControl>
  );
}

SelectTimeDispatch.propTypes = {
  timeOptions: arrayOf(
    shape({
      name: string.isRequired,
      value: stringOrNumberPropTypes
    })
  ).isRequired,
  deliveryOpen: bool,
  deliveryWaitTime: number,
  dispatchDay: object,
  futureOrdersEnabled: bool,
  handleChangeRadioButton: string,
  pickUp: bool,
  pickUpOpen: bool,
  radioButtonSelected: func,
  selectedTime: object,
  onSelectTime: func
};

export default SelectTimeDispatch;
